import { useEffect, useState } from 'react';
import { useMutation, useQuery } from '@apollo/client';
import { useTranslation } from 'react-i18next';
import { Radio, RefreshCw, X } from 'lucide-react';
import {
  Card, CardContent, CardDescription, CardHeader, CardTitle,
} from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import { formatAiredDay, formatDateTime } from '@/lib/format';
import { CLIP_FORMATS, DEFAULT_CLIP_FORMAT } from '@/lib/projects/clipFormats';
import {
  ADMIN_CAMPAIGN_LIVE_IMPORT,
  ADMIN_RUN_CAMPAIGN_LIVE_IMPORT,
  ADMIN_SAVE_CAMPAIGN_LIVE_IMPORT,
} from '@/queries/queries';

interface LiveImportItem {
  id: string;
  video_id: string;
  title: string | null;
  aired_at: string | null;
  status: string;
  email_sent_at: string | null;
  email_recipients: number | null;
}

interface LiveImportView {
  enabled: boolean;
  channel_id: string | null;
  channel_name: string | null;
  channel_thumbnail: string | null;
  keywords: string[];
  format: string;
  language: string;
  prompt: string | null;
  notify_participants: boolean;
  last_checked_at: string | null;
  next_check_at: string | null;
  last_error: string | null;
  items: LiveImportItem[];
}

const LANGUAGES = ['pt-BR', 'en-US', 'es-ES'];
const KNOWN_ERRORS = [
  'already_running', 'campaign_not_found', 'channel_not_found', 'channel_required',
  'keywords_required', 'invalid_keywords', 'invalid_format', 'invalid_language',
];
const T = 'admin.campaigns.edit.liveImport';

const channelUrl = (channelId: string | null) => (
  channelId ? `https://www.youtube.com/channel/${channelId}` : ''
);

const statusKey = (item: LiveImportItem) => (
  item.status === 'added' && item.email_sent_at ? 'added_sent' : item.status
);

/**
 * "Novas lives automáticas": watch one YouTube channel for this campaign and
 * import its finished lives that mention the campaign's words. Saves through
 * its own mutation, separately from the campaign form.
 */
export function CampaignLiveImportCard({ campaignId }: { campaignId: string }) {
  const { t, i18n } = useTranslation();
  const { data } = useQuery(ADMIN_CAMPAIGN_LIVE_IMPORT, {
    variables: { campaignId },
    fetchPolicy: 'cache-and-network',
  });
  const [saveMutation, { loading: saving }] = useMutation(ADMIN_SAVE_CAMPAIGN_LIVE_IMPORT);
  const [runMutation, { loading: running }] = useMutation(ADMIN_RUN_CAMPAIGN_LIVE_IMPORT);

  const [view, setView] = useState<LiveImportView | null>(null);
  const [enabled, setEnabled] = useState(false);
  const [channel, setChannel] = useState('');
  const [keywords, setKeywords] = useState<string[]>([]);
  const [wordDraft, setWordDraft] = useState('');
  const [format, setFormat] = useState<string>(DEFAULT_CLIP_FORMAT);
  const [language, setLanguage] = useState('pt-BR');
  const [prompt, setPrompt] = useState('');
  const [notify, setNotify] = useState(true);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const applyView = (next: LiveImportView | null | undefined) => {
    if (!next) return;
    setView(next);
    setEnabled(next.enabled);
    setChannel(channelUrl(next.channel_id));
    setKeywords(next.keywords || []);
    setFormat(next.format || DEFAULT_CLIP_FORMAT);
    setLanguage(next.language || 'pt-BR');
    setPrompt(next.prompt || '');
    setNotify(next.notify_participants !== false);
  };

  // Reload the form only when the server's settings change, never on a plain
  // re-render, so edits in progress are kept.
  const loadedView = data?.adminCampaignLiveImport;
  useEffect(() => {
    applyView(loadedView);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadedView]);

  const showError = (error: unknown) => {
    const code = error instanceof Error ? error.message : '';
    setErrorMessage(t(`${T}.errors.${KNOWN_ERRORS.includes(code) ? code : 'generic'}`));
  };

  const addWord = () => {
    const word = wordDraft.trim();
    if (word && !keywords.some((k) => k.toLowerCase() === word.toLowerCase())) {
      setKeywords([...keywords, word]);
    }
    setWordDraft('');
  };

  const save = async () => {
    try {
      const res = await saveMutation({
        variables: {
          campaignId,
          input: {
            enabled,
            channel: channel.trim(),
            keywords,
            format,
            language,
            prompt: prompt.trim(),
            notify_participants: notify,
          },
        },
      });
      applyView(res.data?.adminSaveCampaignLiveImport);
    } catch (error) {
      showError(error);
    }
  };

  const checkNow = async () => {
    try {
      const res = await runMutation({ variables: { campaignId } });
      applyView(res.data?.adminRunCampaignLiveImport);
    } catch (error) {
      showError(error);
    }
  };

  return (
    <Card>
      <CardHeader className="flex flex-row items-start justify-between gap-4 space-y-0">
        <div className="space-y-1.5">
          <CardTitle className="flex items-center gap-2">
            <Radio className="h-4 w-4" />
            {t(`${T}.title`)}
          </CardTitle>
          <CardDescription>{t(`${T}.description`)}</CardDescription>
        </div>
        <Switch checked={enabled} onCheckedChange={setEnabled} aria-label={t(`${T}.enabled`)} />
      </CardHeader>
      <CardContent className="space-y-5">
        <div className="space-y-2">
          <Label htmlFor="live-import-channel">{t(`${T}.channel`)}</Label>
          {view?.channel_name && (
            <div className="flex items-center gap-2">
              {view.channel_thumbnail && (
                <img src={view.channel_thumbnail} alt="" className="h-6 w-6 rounded-full object-cover" />
              )}
              <span className="text-sm font-medium">{view.channel_name}</span>
            </div>
          )}
          <Input
            id="live-import-channel"
            value={channel}
            onChange={(e) => setChannel(e.target.value)}
            placeholder={t(`${T}.channelPlaceholder`)}
          />
          <p className="text-xs text-muted-foreground">{t(`${T}.channelHint`)}</p>
        </div>

        <div className="space-y-2">
          <Label htmlFor="live-import-words">{t(`${T}.words`)}</Label>
          <div className="flex flex-wrap gap-2">
            {keywords.map((word) => (
              <span
                key={word}
                className="inline-flex items-center gap-1 rounded-full border border-primary/30 bg-primary/10 px-2.5 py-0.5 text-xs text-primary"
              >
                {word}
                <button
                  type="button"
                  aria-label={`${t(`${T}.removeWord`)} ${word}`}
                  onClick={() => setKeywords(keywords.filter((k) => k !== word))}
                >
                  <X className="h-3 w-3" />
                </button>
              </span>
            ))}
          </div>
          <Input
            id="live-import-words"
            value={wordDraft}
            onChange={(e) => setWordDraft(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ',') {
                e.preventDefault();
                addWord();
              }
            }}
            onBlur={addWord}
            placeholder={t(`${T}.wordsPlaceholder`)}
          />
          <p className="text-xs text-muted-foreground">{t(`${T}.wordsHint`)}</p>
        </div>

        <div className="space-y-2">
          <Label>{t(`${T}.processing`)}</Label>
          <div className="grid gap-3 sm:grid-cols-2">
            <Select value={format} onValueChange={setFormat}>
              <SelectTrigger aria-label={t(`${T}.format`)}><SelectValue /></SelectTrigger>
              <SelectContent>
                {CLIP_FORMATS.map((value) => (
                  <SelectItem key={value} value={value}>{t(`${T}.formats.${value}`)}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={language} onValueChange={setLanguage}>
              <SelectTrigger aria-label={t(`${T}.language`)}><SelectValue /></SelectTrigger>
              <SelectContent>
                {LANGUAGES.map((value) => (
                  <SelectItem key={value} value={value}>{t(`${T}.languages.${value}`)}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <Textarea
            value={prompt}
            onChange={(e) => setPrompt(e.target.value)}
            placeholder={t(`${T}.promptPlaceholder`)}
            rows={3}
          />
        </div>

        <div className="flex items-center justify-between gap-4">
          <div>
            <Label>{t(`${T}.notify`)}</Label>
            <p className="text-xs text-muted-foreground">{t(`${T}.notifyHint`)}</p>
          </div>
          <Switch checked={notify} onCheckedChange={setNotify} aria-label={t(`${T}.notify`)} />
        </div>

        <div className="flex justify-end">
          <Button onClick={save} disabled={saving}>{t(`${T}.save`)}</Button>
        </div>

        <div className="space-y-3 border-t pt-4">
          <div className="flex items-center justify-between gap-4">
            <div>
              <p className="text-sm font-medium">{t(`${T}.latestChecks`)}</p>
              <p className="text-xs text-muted-foreground">
                {view?.last_checked_at
                  ? `${t(`${T}.lastCheck`)} ${formatDateTime(view.last_checked_at, 'America/Sao_Paulo')}`
                  : t(`${T}.neverChecked`)}
                {view?.next_check_at && view.enabled
                  ? ` · ${t(`${T}.nextCheck`)} ${formatDateTime(view.next_check_at, 'America/Sao_Paulo')}`
                  : ''}
              </p>
              {view?.last_error && (
                <p className="text-xs text-destructive">{`${t(`${T}.lastError`)} ${view.last_error}`}</p>
              )}
            </div>
            <Button variant="outline" size="sm" onClick={checkNow} disabled={running} className="gap-1.5">
              <RefreshCw className="h-3.5 w-3.5" />
              {t(`${T}.checkNow`)}
            </Button>
          </div>
          {view?.items?.length ? (
            <ul className="divide-y text-sm">
              {view.items.map((item) => (
                <li key={item.id} className="flex flex-col gap-0.5 py-2 sm:flex-row sm:items-center sm:gap-3">
                  <span className="w-16 shrink-0 text-xs text-muted-foreground">
                    {formatAiredDay(item.aired_at, i18n?.language ?? 'pt') ?? ''}
                  </span>
                  <span className="min-w-0 flex-1 truncate">{item.title}</span>
                  <span className="shrink-0 text-xs text-muted-foreground">
                    {t(`${T}.status.${statusKey(item)}`)}
                  </span>
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-xs text-muted-foreground">{t(`${T}.emptyLog`)}</p>
          )}
        </div>
      </CardContent>

      <Dialog open={!!errorMessage} onOpenChange={() => setErrorMessage(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t(`${T}.errors.title`)}</DialogTitle>
            <DialogDescription>{errorMessage}</DialogDescription>
          </DialogHeader>
        </DialogContent>
      </Dialog>
    </Card>
  );
}
